import React, { useState, useEffect } from 'react';
import {
  Icon,
  CaretLeftIcon,
  CaretRightIcon,
  ListIcon,
} from '@phosphor-icons/react';
import { Card, CardContent } from 'src/components/ui/card';
import { Button } from 'src/components/ui/button';
import { cn } from 'src/lib/utils';

export interface NavigationSubsection {
  id: string;
  label: string;
  ref: React.RefObject<HTMLDivElement | null>;
}

export interface NavigationSection {
  id: string;
  label: string;
  icon: Icon;
  ref: React.RefObject<HTMLDivElement | null>;
  hasData?: boolean;
  isExpanded?: boolean;
  subsections?: NavigationSubsection[];
}

interface PageNavigatorProps {
  sections: NavigationSection[];
  onNavigate: (sectionId: string) => void;
  onToggleSection?: (sectionId: string) => void;
  className?: string;
  mode?: 'overlay' | 'sidebar';
  onSidebarToggle?: (isExpanded: boolean) => void;
  storageKey?: string; // Custom localStorage key for state persistence
  positioning?: 'overlay' | 'static'; // Whether sidebar overlays content or takes up space
  title?: string; // Title for the navigation sidebar
}

export const PageNavigator: React.FC<PageNavigatorProps> = ({
  sections,
  onNavigate,
  onToggleSection: _onToggleSection,
  className,
  mode = 'overlay',
  onSidebarToggle,
  storageKey = 'pageNavigatorCollapsed',
  positioning = 'overlay',
  title = 'Page Navigation',
}) => {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (mode === 'sidebar') {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : false;
    }
    return false;
  });

  // Save state to localStorage for sidebar mode
  useEffect(() => {
    if (mode === 'sidebar') {
      localStorage.setItem(storageKey, JSON.stringify(isCollapsed));
      onSidebarToggle?.(isCollapsed);
    }
  }, [isCollapsed, mode, onSidebarToggle, storageKey]);

  const handleSectionClick = (section: NavigationSection) => {
    if (section.hasData === false) return;

    // Always navigate to the section - let the parent handle expansion/scrolling
    onNavigate(section.id);
  };

  // Render based on mode
  if (mode === 'sidebar') {
    const isOverlay = positioning === 'overlay';

    return (
      <>
        {/* FAB Button when collapsed - always an overlay */}
        {isCollapsed && (
          <div className="fixed right-4 top-1/2 -translate-y-1/2 z-50">
            <Button
              variant="outline"
              size="sm"
              className="h-12 w-12 p-0 shadow-lg bg-white border-2 rounded-full hover:shadow-xl transition-all"
              onClick={() => setIsCollapsed(false)}
            >
              <ListIcon className="h-5 w-5" />
            </Button>
          </div>
        )}

        {/* Sidebar when expanded */}
        {!isCollapsed && (
          <div
            className={cn(
              'w-80 bg-background shadow-2xl flex flex-col flex-shrink-0',
              isOverlay
                ? 'fixed right-0 top-4 max-h-[calc(100vh-2rem)] z-50 border border-border rounded-l-lg'
                : 'sticky top-4 border border-border self-start rounded-lg',
            )}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                {title}
              </h3>
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 border hover:bg-accent"
                onClick={() => setIsCollapsed(true)}
              >
                <CaretRightIcon className="h-4 w-4" />
              </Button>
            </div>

            {/* Navigation Content */}
            <div
              className={cn(
                'p-4',
                isOverlay && 'overflow-y-auto flex-shrink min-h-0',
              )}
            >
              <nav className="space-y-2">
                {sections.map((section) => {
                  const Icon = section.icon;
                  const isDisabled = section.hasData === false;
                  const hasSubsections =
                    section.subsections && section.subsections.length > 0;
                  const showSubsections = hasSubsections && section.isExpanded;

                  return (
                    <div key={section.id} className="space-y-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          'w-full justify-start gap-3 h-auto py-3 px-3 text-left transition-colors hover:bg-accent border border-transparent hover:border-border',
                          isDisabled && 'opacity-50 cursor-not-allowed',
                        )}
                        onClick={() => handleSectionClick(section)}
                        disabled={isDisabled}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                        <span className="text-sm font-medium truncate">
                          {section.label}
                        </span>
                      </Button>

                      {/* Subsections */}
                      {showSubsections && (
                        <div className="ml-4 space-y-1 border-l-2 border-border pl-4">
                          {section.subsections?.map((subsection) => (
                            <Button
                              key={subsection.id}
                              variant="ghost"
                              size="sm"
                              className="w-full justify-start gap-2 h-auto py-2 px-3 text-left transition-colors hover:bg-accent text-xs border border-transparent hover:border-border"
                              onClick={() => onNavigate(subsection.id)}
                            >
                              <div className="h-2 w-2 rounded-full bg-muted-foreground flex-shrink-0" />
                              <span className="truncate text-muted-foreground">
                                {subsection.label}
                              </span>
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </div>
          </div>
        )}
      </>
    );
  }

  // Original overlay mode
  if (isCollapsed) {
    return (
      <div
        className={cn('fixed right-6 top-1/2 -translate-y-1/2 z-50', className)}
      >
        <Button
          variant="outline"
          size="sm"
          className="h-10 w-10 p-0 shadow-lg bg-white border-2 rounded-full"
          onClick={() => setIsCollapsed(false)}
        >
          <CaretLeftIcon className="h-5 w-5" />
        </Button>
      </div>
    );
  }

  return (
    <div
      className={cn('fixed right-6 top-1/2 -translate-y-1/2 z-50', className)}
    >
      <Card className="w-64 shadow-lg border-2">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              {title}
            </h3>
            <Button
              variant="outline"
              size="sm"
              className="h-6 w-6 p-0 border hover:bg-accent"
              onClick={() => setIsCollapsed(true)}
            >
              <CaretRightIcon className="h-4 w-4" />
            </Button>
          </div>
          <nav className="space-y-1">
            {sections.map((section) => {
              const Icon = section.icon;
              const isDisabled = section.hasData === false;
              const hasSubsections =
                section.subsections && section.subsections.length > 0;
              const showSubsections = hasSubsections && section.isExpanded;

              return (
                <div key={section.id} className="space-y-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn(
                      'w-full justify-start gap-3 h-auto py-2 px-3 text-left transition-colors hover:bg-accent border border-transparent hover:border-border',
                      isDisabled && 'opacity-50 cursor-not-allowed',
                    )}
                    onClick={() => handleSectionClick(section)}
                    disabled={isDisabled}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm truncate">{section.label}</span>
                  </Button>

                  {/* Subsections */}
                  {showSubsections && (
                    <div className="ml-4 space-y-1 border-l border-border pl-3">
                      {section.subsections?.map((subsection) => (
                        <Button
                          key={subsection.id}
                          variant="ghost"
                          size="sm"
                          className="w-full justify-start gap-2 h-auto py-1.5 px-2 text-left transition-colors hover:bg-accent text-xs border border-transparent hover:border-border"
                          onClick={() => onNavigate(subsection.id)}
                        >
                          <div className="h-2 w-2 rounded-full bg-muted-foreground flex-shrink-0" />
                          <span className="truncate">{subsection.label}</span>
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </CardContent>
      </Card>
    </div>
  );
};

export default PageNavigator;
